import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useServiceChargePeriod, useServiceChargePeriods, useServiceChargeMutations } from '@/hooks/serviceCharge';
import { ServiceChargeWeightsForm } from './ServiceChargeWeightsForm';
import { serviceChargeService } from '@/services/compensation/serviceChargeService';
import type { ServiceChargeWeightInput } from '@/services/compensation/serviceChargeService';
import { Loader2, AlertCircle, Calendar, Copy } from 'lucide-react';
import { format, lastDayOfMonth } from 'date-fns';
import { toast } from 'sonner';

const periodSchema = z.object({
  period_label: z.string().min(1, 'Period label is required'),
  period_start: z.string().min(1, 'Start date is required'),
  period_end: z.string().min(1, 'End date is required'),
  total_pool_amount: z.coerce.number().min(0, 'Amount must be positive'),
  staff_allocation_percent: z.coerce.number().min(0).max(100),
  loss_breakage_percent: z.coerce.number().min(0).max(100),
  training_percent: z.coerce.number().min(0).max(100),
  exclude_days_below_hours: z.coerce.number().optional().nullable(),
}).refine(
  (data) => {
    const total = data.staff_allocation_percent + data.loss_breakage_percent + data.training_percent;
    return Math.abs(total - 100) < 0.01;
  },
  {
    message: 'Allocation percentages must sum to 100%',
    path: ['staff_allocation_percent'],
  }
).refine(
  (data) => new Date(data.period_start) <= new Date(data.period_end),
  {
    message: 'End date must be after start date',
    path: ['period_end'],
  }
);

type PeriodFormData = z.infer<typeof periodSchema>;

interface ServiceChargePeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId?: string | null;
}

export const ServiceChargePeriodDialog = ({
  open,
  onOpenChange,
  periodId,
}: ServiceChargePeriodDialogProps) => {
  const [weights, setWeights] = useState<ServiceChargeWeightInput[]>([]);
  const [dateConflict, setDateConflict] = useState<string | null>(null);
  
  const { data: period, isLoading } = useServiceChargePeriod(periodId || '', !!periodId);
  const { data: allPeriods } = useServiceChargePeriods();
  const { createPeriod, updatePeriod, updatePeriodWeights } = useServiceChargeMutations();

  // Defense-in-depth: prevent editing active/finalized periods (period fields only)
  // Weight categories are editable for draft AND active periods, but not finalized
  const isLocked = period?.status === 'finalized';
  const isPeriodFieldsLocked = period && (period.status === 'active' || period.status === 'finalized');
  const isWeightsEditable = period ? period.status !== 'finalized' : true;

  // Month quick-select: generate months for current year
  const currentYear = new Date().getFullYear();
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(currentYear, i, 1);
    return {
      value: i.toString(),
      label: format(date, 'MMMM yyyy'),
      start: format(date, 'yyyy-MM-dd'),
      end: format(lastDayOfMonth(date), 'yyyy-MM-dd'),
    };
  });

  const handleMonthSelect = (monthIndex: string) => {
    const opt = monthOptions[parseInt(monthIndex)];
    if (!opt) return;
    form.setValue('period_label', opt.label, { shouldValidate: true });
    form.setValue('period_start', opt.start, { shouldValidate: true, shouldDirty: true });
    form.setValue('period_end', opt.end, { shouldValidate: true, shouldDirty: true });
  };

  // Weight template: load from existing period and append
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const periodsWithWeightOptions = (allPeriods || []).filter(p => periodId ? p.id !== periodId : true);

  const handleLoadWeightTemplate = useCallback(async (sourcePeriodId: string) => {
    setIsLoadingTemplate(true);
    try {
      const source = await serviceChargeService.getPeriodWithWeights(sourcePeriodId);
      if (!source || source.weights.length === 0) {
        toast.info('Selected period has no weight categories');
        return;
      }
      const templateWeights: ServiceChargeWeightInput[] = source.weights.map((w, idx) => ({
        period_id: '',
        weight_label: w.weight_label,
        weight_multiplier: Number(w.weight_multiplier) || 1.0,
        display_order: weights.length + idx + 1,
      }));
      setWeights(prev => [...prev, ...templateWeights]);
      toast.success(`Added ${templateWeights.length} weight categories from "${source.period_label}"`);
    } catch {
      toast.error('Failed to load weight template');
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [weights.length]);

  const form = useForm<PeriodFormData>({
    resolver: zodResolver(periodSchema),
    defaultValues: {
      period_label: '',
      period_start: '',
      period_end: '',
      total_pool_amount: 0,
      staff_allocation_percent: 93,
      loss_breakage_percent: 5,
      training_percent: 2,
      exclude_days_below_hours: null,
    },
  });

  useEffect(() => {
    if (period) {
      form.reset({
        period_label: period.period_label,
        period_start: period.period_start,
        period_end: period.period_end,
        total_pool_amount: Number(period.total_pool_amount),
        staff_allocation_percent: Number(period.staff_allocation_percent),
        loss_breakage_percent: Number(period.loss_breakage_percent),
        training_percent: Number(period.training_percent),
        exclude_days_below_hours: period.exclude_days_below_hours ? Number(period.exclude_days_below_hours) : null,
      });
      setWeights(
        period.weights.map((w, idx) => ({
          id: w.id,
          period_id: period.id,
          weight_label: w.weight_label,
          weight_multiplier: Number(w.weight_multiplier) || 1.0,
          display_order: w.display_order || idx + 1,
        }))
      );
    } else {
      form.reset({
        period_label: '',
        period_start: '',
        period_end: '',
        total_pool_amount: 0,
        staff_allocation_percent: 93,
        loss_breakage_percent: 5,
        training_percent: 2,
        exclude_days_below_hours: null,
      });
      setWeights([]);
    }
  }, [period, form]);

  // Check for date conflicts when dates change
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'period_start' || name === 'period_end') {
        const startDate = value.period_start;
        const endDate = value.period_end;
        
        if (startDate && endDate && allPeriods) {
          const conflict = allPeriods.find(p => {
            // Skip checking against the current period being edited
            if (periodId && p.id === periodId) return false;
            
            return p.period_start === startDate && p.period_end === endDate;
          });
          
          if (conflict) {
            setDateConflict(`Period "${conflict.period_label}" already exists for these dates`);
          } else {
            setDateConflict(null);
          }
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, allPeriods, periodId]);

  const onSubmit = async (data: PeriodFormData) => {
    // Defense-in-depth: block submission for finalized periods
    if (isLocked) return;

    // Client-side validation for duplicate periods
    if (dateConflict && !periodId) {
      return; // Prevent submission if there's a conflict
    }
    
    try {
      if (periodId) {
        // Only update period fields if period is draft (active periods have field protection)
        if (period?.status === 'draft') {
          await updatePeriod.mutateAsync({ 
            periodId, 
            updates: data as any
          });
        }
        // Update weights for both draft and active periods
        if (isWeightsEditable) {
          await updatePeriodWeights.mutateAsync({
            periodId,
            weights,
          });
        }
      } else {
        await createPeriod.mutateAsync({ 
          period: data as any, 
          weights 
        });
      }
      onOpenChange(false);
      form.reset();
      setWeights([]);
      setDateConflict(null);
    } catch (error) {
      console.error('Failed to save period:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {periodId ? 'Edit Service Charge Period' : 'Create Service Charge Period'}
          </DialogTitle>
          <DialogDescription>
            Configure the monthly service charge pool and allocation percentages.
          </DialogDescription>
        </DialogHeader>
        
        {/* Show existing periods for reference */}
        {!periodId && allPeriods && allPeriods.length > 0 && (
          <Alert>
            <Calendar className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Existing periods:</p>
                <div className="text-sm space-y-1">
                  {allPeriods.slice(-3).map(p => (
                    <div key={p.id} className="text-muted-foreground">
                      • {p.period_label} ({format(new Date(p.period_start), 'MMM d, yyyy')} - {format(new Date(p.period_end), 'MMM d, yyyy')})
                    </div>
                  ))}
                  {allPeriods.length > 3 && (
                    <div className="text-muted-foreground">... and {allPeriods.length - 3} more</div>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {isLoading && periodId ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : isLocked ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This period is {period?.status} and cannot be edited. Deactivate it first to make changes.
            </AlertDescription>
          </Alert>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Show date conflict warning */}
              {dateConflict && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {dateConflict}. Please choose different dates or edit the existing period instead.
                  </AlertDescription>
                </Alert>
              )}
              
              {isPeriodFieldsLocked && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Period fields are locked while {period?.status}. Only weight categories can be edited.
                  </AlertDescription>
                </Alert>
              )}

              {/* Month quick-select */}
              {!isPeriodFieldsLocked && (
                <div className="space-y-2">
                  <FormLabel>Quick Select Month</FormLabel>
                  <Select onValueChange={handleMonthSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a month to auto-fill dates" />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="period_label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Period Label</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., June 2025" {...field} disabled={!!isPeriodFieldsLocked} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="total_pool_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Pool Amount</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="0.00" {...field} disabled={!!isPeriodFieldsLocked} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="period_start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} disabled={!!isPeriodFieldsLocked} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="period_end"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} disabled={!!isPeriodFieldsLocked} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Allocation Percentages (must sum to 100%)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="staff_allocation_percent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Staff %</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} disabled={!!isPeriodFieldsLocked} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="loss_breakage_percent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Loss & Breakage %</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} disabled={!!isPeriodFieldsLocked} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="training_percent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Training %</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} disabled={!!isPeriodFieldsLocked} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="exclude_days_below_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Exclude Days Below Hours (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="Leave empty to include all worked days"
                        {...field}
                        value={field.value ?? ''}
                        disabled={!!isPeriodFieldsLocked}
                      />
                    </FormControl>
                    <FormDescription>
                      Days where an employee worked fewer than this many hours will be excluded from the distribution. Leave empty to include all worked days.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isWeightsEditable && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Weight Categories</h3>
                    {periodsWithWeightOptions.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Select onValueChange={handleLoadWeightTemplate} disabled={isLoadingTemplate}>
                          <SelectTrigger className="w-[220px] h-8 text-xs">
                            <Copy className="h-3 w-3 mr-1" />
                            <SelectValue placeholder="Load from period..." />
                          </SelectTrigger>
                          <SelectContent>
                            {periodsWithWeightOptions.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.period_label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isLoadingTemplate && <Loader2 className="h-4 w-4 animate-spin" />}
                      </div>
                    )}
                  </div>
                  <ServiceChargeWeightsForm weights={weights} onChange={setWeights} />
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createPeriod.isPending || updatePeriod.isPending || updatePeriodWeights.isPending || (!!dateConflict && !periodId)}
                >
                  {createPeriod.isPending || updatePeriod.isPending || updatePeriodWeights.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : periodId ? (
                    'Update Period'
                  ) : (
                    'Create Period'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
